import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api, { brl, mediaUrl, timeAgo, formatApiError } from "@/lib/api";
import { Pill, ORDER_STATUS } from "@/components/market/MarketUI";
import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const act = async (url, body, msg, reload) => {
  try { await api.post(url, body); toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
};

export function MarketAdminTab() {
  return (
    <div className="space-y-10">
      <Sellers />
      <Listings />
      <Orders />
    </div>
  );
}

function Section({ title, children }) {
  return <section><h3 className="mb-3 font-bold">{title}</h3>{children}</section>;
}

function Sellers() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/market/sellers", { params: { status: "pending" } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Section title="Vendedores aguardando aprovação">
      {items === null ? <Loader /> : !items.length ? <p className="text-sm text-muted-foreground" data-testid="no-pending-sellers">Nenhuma solicitação pendente.</p> : items.map((u) => (
        <div key={u.id} data-testid={`pending-seller-${u.id}`} className="mb-2 flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center">
          <img src={mediaUrl(u.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{u.seller?.store_name} <span className="font-normal text-muted-foreground">· {u.name} ({u.email})</span></p>
            <p className="text-xs text-muted-foreground">{u.seller?.city}/{u.seller?.state} · {u.seller?.phone}</p>
            {u.seller?.about && <p className="mt-1 text-xs">{u.seller.about}</p>}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full gap-1" data-testid={`approve-seller-${u.id}`} onClick={() => act(`/admin/market/sellers/${u.id}/approve`, null, "Vendedor aprovado", load)}><Check className="h-3.5 w-3.5" /> Aprovar</Button>
            <Button size="sm" variant="outline" className="rounded-full gap-1 text-destructive" data-testid={`reject-seller-${u.id}`} onClick={() => act(`/admin/market/sellers/${u.id}/reject`, null, "Solicitação recusada", load)}><X className="h-3.5 w-3.5" /> Recusar</Button>
          </div>
        </div>
      ))}
    </Section>
  );
}

function Listings() {
  const [items, setItems] = useState(null);
  const [reasons, setReasons] = useState({});
  const load = () => api.get("/admin/market/listings", { params: { status: "pending" } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Section title="Anúncios aguardando moderação">
      {items === null ? <Loader /> : !items.length ? <p className="text-sm text-muted-foreground" data-testid="no-pending-listings">Nenhum anúncio pendente.</p> : items.map((l) => (
        <div key={l.id} data-testid={`pending-listing-${l.id}`} className="mb-2 flex flex-col gap-3 rounded-xl border border-border bg-card p-3 sm:flex-row sm:items-center">
          <img src={mediaUrl(l.images?.[0])} alt="" className="h-14 w-16 rounded-lg object-cover" />
          <div className="flex-1 min-w-0">
            <Link to={`/mercado/anuncio/${l.id}`} className="text-sm font-semibold hover:text-primary">{l.title}</Link>
            <p className="text-xs text-muted-foreground">{brl(l.price)} · {l.seller?.store_name} · {l.city}/{l.state}</p>
          </div>
          <Input value={reasons[l.id] || ""} onChange={(e) => setReasons((r) => ({ ...r, [l.id]: e.target.value }))} placeholder="Motivo (se recusar)" className="h-8 rounded-lg sm:w-48" data-testid={`reject-reason-${l.id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full gap-1" data-testid={`approve-listing-${l.id}`} onClick={() => act(`/admin/market/listings/${l.id}/approve`, null, "Anúncio aprovado", load)}><Check className="h-3.5 w-3.5" /> Aprovar</Button>
            <Button size="sm" variant="outline" className="rounded-full gap-1 text-destructive" data-testid={`reject-listing-${l.id}`} onClick={() => act(`/admin/market/listings/${l.id}/reject`, { reason: reasons[l.id] || "" }, "Anúncio recusado", load)}><X className="h-3.5 w-3.5" /> Recusar</Button>
          </div>
        </div>
      ))}
    </Section>
  );
}

function Orders() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/market/orders").then((r) => setItems(r.data)); }, []);
  const total = (items || []).reduce((s, o) => s + o.total, 0);
  return (
    <Section title={`Pedidos pagos${items ? ` · ${brl(total)}` : ""}`}>
      {items === null ? <Loader /> : !items.length ? <p className="text-sm text-muted-foreground">Nenhum pedido pago ainda.</p> : items.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.id}`} className="mb-2 flex items-center gap-3 rounded-xl border border-border bg-card p-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold line-clamp-1">{o.quantity}x {o.listing_title}</p>
            <p className="text-xs text-muted-foreground">{o.buyer?.name} → {o.seller?.store_name} · {timeAgo(o.created_at)}</p>
          </div>
          <span className="text-sm font-bold">{brl(o.total)}</span>
          <Pill map={ORDER_STATUS} value={o.status} />
        </div>
      ))}
    </Section>
  );
}
